if (!customElements.get('rs3gt-product')) {
  customElements.define('rs3gt-product', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('rs7gt-launch');
      this.viewport = this.querySelector('[data-rs3-scroll]');
      this.videos = Object.fromEntries([...this.querySelectorAll('[data-rs3-video]')]
        .map(video => [video.dataset.rs3Video, video]));
      this.sourceQuery = matchMedia('(max-width: 749px)');
      this.mediaMode = this.sourceQuery.matches ? 'mobile' : 'desktop';
      this.fps = Number(this.dataset.fps) || 30;
      this.duration = Number(this.dataset.duration) || 4;
      this.frameCount = Math.round(this.duration * this.fps) + 1;
      this.buttons = [...this.querySelectorAll('[data-rs3-color]')];
      this.stages = this.buildStages();
      this.panels = [...this.querySelectorAll('[data-rs3-panel]')];
      this.purchasePanels = [...this.querySelectorAll('[data-rs3-purchase]')];
      this.label = this.querySelector('[data-rs3-color-name]');
      this.status = this.querySelector('[data-rs3-status]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.designMode = this.root?.dataset.designMode === 'true';
      this.controller = new AbortController();
      this.failed = !this.viewport || !this.videos.forward || !this.videos.reverse || !this.stages.length
        || !this.videos.forward.canPlayType('video/mp4; codecs="avc1.640028"');
      this.desiredStage = 0;
      this.settledStage = 0;
      this.playhead = this.stages[0]?.frame || 0;
      this.presentedFrame = -1;
      this.transition = null;
      this.wheelIntentAt = 0;
      this.wheelSettledAt = 0;
      this.currentId = this.buttons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.rs3Color;
      this.dataset.rs3MediaMode = this.mediaMode;
      const options = { signal: this.controller.signal };
      this.buttons.forEach(button => { button.disabled = false; });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-rs3-color]');
        if (button && this.contains(button)) this.select(button.dataset.rs3Color);
      }, options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      document.addEventListener('shopify:block:select', event => {
        if (event.detail?.blockId) this.select(event.detail.blockId);
      }, options);
      this.reducedMotion.addEventListener('change', () => this.measure(), options);
      this.sourceQuery.addEventListener('change', () => this.changeSource(), options);
      this.root?.addEventListener('rs7gt:motionchange', () => this.measure(), options);
      this.root?.addEventListener('rs7gt:pagecoveragechange', () => this.requestFrame(), options);
      Object.values(this.videos).forEach(video => {
        video.muted = true;
        video.playsInline = true;
        video.addEventListener('error', () => {
          if (this.media && video.currentSrc) this.failMedia();
        }, options);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.releaseMedia();
        else this.requestFrame();
      }, options);
      this.nearObserver = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) this.loadMedia();
        else this.releaseMedia();
      }, { rootMargin: '200% 0px' });
      this.nearObserver.observe(this);
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.requestFrame();
      });
      this.visibleObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      this.measure();
    }

    get endTime() { return this.duration; }
    get targetFrame() { return Math.round(this.playhead); }
    get busy() { return this.enabled && (!!this.transition || this.desiredStage !== this.settledStage); }

    buildStages() {
      return this.buttons.map(button => ({
        id: button.dataset.rs3Color,
        frame: Math.max(0, Math.min(this.frameCount - 1, Math.round(Number(button.dataset.videoTime) * this.fps) || 0))
      })).sort((a, b) => a.frame - b.frame);
    }

    sourceURL(direction, mode = this.mediaMode) {
      const suffix = `${mode[0].toUpperCase()}${mode.slice(1)}${direction[0].toUpperCase()}${direction.slice(1)}`;
      return this.dataset[`video${suffix}`] || '';
    }

    changeSource() {
      const mode = this.sourceQuery.matches ? 'mobile' : 'desktop';
      if (mode === this.mediaMode) return;
      const selectedId = this.stages[this.desiredStage]?.id || this.currentId;
      this.releaseMedia();
      this.mediaMode = mode;
      this.dataset.rs3MediaMode = mode;
      const selectedStage = Math.max(0, this.stages.findIndex(stage => stage.id === selectedId));
      this.desiredStage = selectedStage;
      this.settledStage = selectedStage;
      this.playhead = this.stages[selectedStage]?.frame || 0;
      this.presentedFrame = -1;
      this.transition = null;
      this.showColor(this.stages[selectedStage]?.id);
      this.measure();
    }

    advanceColor(direction) {
      if (!this.enabled) return false;
      if (this.busy) return true;
      const stage = this.settledStage + direction;
      if (stage < 0 || stage >= this.stages.length) return false;
      this.desiredStage = stage;
      this.requestFrame();
      return true;
    }

    reserveColor(direction) {
      if (!this.enabled) return false;
      if (!this.busy) return this.advanceColor(direction);
      // A second physical gesture may reserve one following leg, but it can
      // never interrupt, reverse or build an unbounded queue behind the clip.
      const activeDirection = this.transition?.direction
        || Math.sign(this.desiredStage - this.settledStage);
      if (direction !== activeDirection || Math.abs(this.desiredStage - this.settledStage) >= 2) return true;
      const stage = this.desiredStage + direction;
      if (stage < 0 || stage >= this.stages.length) return true;
      this.desiredStage = stage;
      this.requestFrame();
      return true;
    }

    measure() {
      if (!this.controller || !this.viewport) return;
      this.enabled = !this.reducedMotion.matches && !this.designMode && !this.failed;
      this.toggleAttribute('data-rs3-scroll-ready', this.enabled);
      this.toggleAttribute('data-rs3-media-error', this.failed);
      if (this.enabled) {
        this.loadMedia();
      } else {
        this.transition = null;
        this.settledStage = this.desiredStage;
        this.playhead = this.stages[this.settledStage]?.frame || 0;
        this.presentedFrame = this.playhead;
        this.showColor(this.stages[this.settledStage]?.id);
        this.releaseMedia();
        this.viewport.scrollTop = 0;
      }
      this.requestFrame();
    }

    loadMedia() {
      if (!this.enabled || !this.near || this.media || document.hidden) return;
      const controller = new AbortController();
      this.media = { controller, mode: this.mediaMode, active: null, priming: true };
      for (const video of Object.values(this.videos)) {
        video.pause();
        video.removeAttribute('data-rs3-active');
        video.removeAttribute('src');
        video.preload = 'none';
      }
      this.ensureSource(this.preferredDirection());
      this.primeStage(controller.signal).catch(error => {
        if (error.name !== 'AbortError' && this.media?.controller === controller) this.failMedia();
      });
    }

    preferredDirection() {
      return this.settledStage >= this.stages.length - 1 ? 'reverse' : 'forward';
    }

    ensureSource(direction) {
      const video = this.videos[direction];
      if (!video) return null;
      const source = this.sourceURL(direction);
      if (!source) return video;
      video.preload = 'auto';
      if (video.getAttribute('src') !== source) {
        video.src = source;
        video.load();
      } else if (video.readyState < 1) {
        video.load();
      }
      return video;
    }

    async primeStage(signal) {
      const direction = this.preferredDirection();
      const video = this.ensureSource(direction);
      await this.prepareVideo(video, this.mediaTime(direction, this.stages[this.settledStage].frame), signal);
      if (signal.aborted || !this.media) throw new DOMException('Aborted', 'AbortError');
      this.activateVideo(direction);
      this.playhead = this.stages[this.settledStage].frame;
      this.presentedFrame = this.playhead;
      this.media.priming = false;
      this.setAttribute('data-rs3-media-ready', '');
      this.requestFrame();
    }

    mediaTime(direction, frame) {
      const canonical = Math.max(0, Math.min(this.duration, frame / this.fps));
      return direction === 'forward' ? canonical : this.duration - canonical;
    }

    async prepareVideo(video, time, signal) {
      video.preload = 'auto';
      if (video.readyState < 1) await this.waitFor(video, 'loadedmetadata', signal, 15000);
      await this.seekVideo(video, time, signal);
      if (video.readyState < 2) await this.waitFor(video, 'loadeddata', signal, 15000);
    }

    async seekVideo(video, time, signal) {
      const target = Math.max(0, Math.min(Number.isFinite(video.duration) ? video.duration : this.duration, time));
      if (Math.abs(video.currentTime - target) <= 1 / (this.fps * 2)) return;
      const complete = this.waitFor(video, 'seeked', signal, 10000);
      video.currentTime = target;
      await complete;
    }

    waitFor(target, eventName, signal, timeout) {
      return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
        let timer;
        const cleanup = () => {
          clearTimeout(timer);
          target.removeEventListener(eventName, done);
          target.removeEventListener('error', failed);
          signal.removeEventListener('abort', aborted);
        };
        const done = () => { cleanup(); resolve(); };
        const failed = () => { cleanup(); reject(new Error(`Video ${eventName} failed`)); };
        const aborted = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };
        target.addEventListener(eventName, done, { once: true });
        target.addEventListener('error', failed, { once: true });
        signal.addEventListener('abort', aborted, { once: true });
        timer = setTimeout(() => { cleanup(); reject(new Error(`Video ${eventName} timed out`)); }, timeout);
      });
    }

    rangeBuffered(video, from, to) {
      const start = Math.min(from, to);
      const end = Math.max(from, to);
      const tolerance = 1 / this.fps;
      for (let index = 0; index < video.buffered.length; index++) {
        if (video.buffered.start(index) <= start + tolerance && video.buffered.end(index) >= end - tolerance) return true;
      }
      return false;
    }

    waitForBuffer(video, from, to, signal) {
      if (this.rangeBuffered(video, from, to)) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const started = performance.now();
        const check = () => {
          if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
          if (this.rangeBuffered(video, from, to)) { resolve(); return; }
          if (performance.now() - started > 45000) { reject(new Error('Video buffer timed out')); return; }
          setTimeout(check, 100);
        };
        check();
      });
    }

    activateVideo(direction) {
      if (!this.media) return;
      this.media.active = direction;
      for (const [name, video] of Object.entries(this.videos)) video.toggleAttribute('data-rs3-active', name === direction);
    }

    warmVideo(direction) {
      this.ensureSource(direction);
    }

    releaseMedia() {
      this.removeAttribute('data-rs3-media-ready');
      this.removeAttribute('data-rs3-media-buffering');
      this.cancelPlaybackMonitor();
      const media = this.media;
      this.media = null;
      this.wheelIntentAt = 0;
      this.wheelSettledAt = 0;
      if (media) media.controller.abort();
      for (const video of Object.values(this.videos || {})) {
        video.pause();
        video.removeAttribute('data-rs3-active');
        video.removeAttribute('src');
        video.preload = 'none';
        video.load();
      }
      if (this.transition) {
        this.transition = null;
        this.playhead = this.stages[this.settledStage]?.frame || 0;
        this.presentedFrame = -1;
        this.showColor(this.stages[this.settledStage]?.id);
      }
    }

    failMedia() {
      if (this.failed) return;
      this.failed = true;
      this.setAttribute('data-rs3-media-error', '');
      this.measure();
    }

    requestFrame() {
      if (this.frame || !this.controller) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.drive();
      });
    }

    drive() {
      if (!this.enabled || document.hidden) return;
      this.loadMedia();
      if (!this.visible || this.root?.sceneVisibility?.(this) === 0 || !this.media || this.media.priming) return;
      if (!this.transition && this.desiredStage !== this.settledStage) this.beginTransition();
    }

    beginTransition() {
      const step = Math.sign(this.desiredStage - this.settledStage);
      if (!step || !this.media) return;
      const stage = this.settledStage + step;
      const direction = step > 0 ? 'forward' : 'reverse';
      const from = this.stages[this.settledStage].frame;
      const to = this.stages[stage].frame;
      const transition = this.transition = { stage, from, to, direction: step, source: direction, running: false };
      const signal = this.media.controller.signal;
      const video = this.ensureSource(direction);
      const fromTime = this.mediaTime(direction, from);
      const toTime = this.mediaTime(direction, to);
      this.setAttribute('data-rs3-media-buffering', '');
      this.prepareVideo(video, fromTime, signal)
        .then(() => {
          if (this.transition !== transition || signal.aborted) throw new DOMException('Aborted', 'AbortError');
          this.activateVideo(direction);
          this.setAttribute('data-rs3-media-ready', '');
          return this.waitForBuffer(video, fromTime, toTime, signal);
        })
        .then(() => {
          if (this.transition !== transition || signal.aborted) throw new DOMException('Aborted', 'AbortError');
          this.removeAttribute('data-rs3-media-buffering');
          video.playbackRate = 1;
          this.armPlaybackTimeout(transition);
          return video.play();
        })
        .then(() => {
          if (this.transition === transition && !signal.aborted) {
            transition.running = true;
            this.monitorTransition(video, transition, toTime);
            if (this.desiredStage === transition.stage) {
              this.warmVideo(direction === 'forward' ? 'reverse' : 'forward');
            }
          }
        })
        .catch(error => {
          if (error.name !== 'AbortError' && this.transition === transition) this.failMedia();
        });
    }

    monitorTransition(video, transition, targetTime) {
      this.cancelPlaybackMonitor();
      this.playbackVideo = video;
      this.armPlaybackTimeout(transition);
      const sample = mediaTime => {
        if (this.transition !== transition || !transition.running) return;
        const canonical = transition.source === 'forward' ? mediaTime : this.duration - mediaTime;
        this.playhead = Math.max(0, Math.min(this.frameCount - 1, canonical * this.fps));
        this.presentedFrame = Math.round(this.playhead);
        this.updateColorForTime(this.playhead / this.fps);
        if (mediaTime >= targetTime - .45 / this.fps || video.ended) {
          this.finishTransition(video, transition, targetTime);
          return;
        }
        this.schedulePlaybackSample(video, sample);
      };
      this.schedulePlaybackSample(video, sample);
    }

    armPlaybackTimeout(transition) {
      clearTimeout(this.playbackTimeout);
      this.playbackTimeout = setTimeout(() => {
        if (this.transition === transition) this.failMedia();
      }, 8000);
    }

    schedulePlaybackSample(video, sample) {
      if ('requestVideoFrameCallback' in video) {
        this.videoFrameCallback = video.requestVideoFrameCallback((_, metadata) => {
          this.videoFrameCallback = 0;
          sample(metadata.mediaTime);
        });
      } else {
        this.playbackFrame = requestAnimationFrame(() => {
          this.playbackFrame = 0;
          sample(video.currentTime);
        });
      }
    }

    finishTransition(video, transition, targetTime) {
      video.pause();
      transition.running = false;
      clearTimeout(this.playbackTimeout);
      this.playbackTimeout = 0;
      const signal = this.media?.controller.signal;
      if (!signal) return;
      this.seekVideo(video, targetTime, signal)
        .then(() => {
          if (this.transition !== transition || signal.aborted) return;
          this.playhead = transition.to;
          this.presentedFrame = transition.to;
          this.settledStage = transition.stage;
          if (this.wheelIntentAt && this.desiredStage === transition.stage) {
            this.wheelSettledAt = performance.now();
            this.wheelIntentAt = 0;
          }
          this.transition = null;
          this.removeAttribute('data-rs3-media-buffering');
          this.showColor(this.stages[this.settledStage]?.id);
          this.requestFrame();
        })
        .catch(error => {
          if (error.name !== 'AbortError' && this.transition === transition) this.failMedia();
        });
    }

    cancelPlaybackMonitor() {
      if (this.videoFrameCallback && this.playbackVideo?.cancelVideoFrameCallback) {
        this.playbackVideo.cancelVideoFrameCallback(this.videoFrameCallback);
      }
      cancelAnimationFrame(this.playbackFrame);
      clearTimeout(this.playbackTimeout);
      this.videoFrameCallback = 0;
      this.playbackFrame = 0;
      this.playbackTimeout = 0;
      this.playbackVideo = null;
    }

    updateColorForTime(time) {
      const button = this.buttons.filter(candidate => Number(candidate.dataset.colorStart) <= time + 0.001)
        .sort((a, b) => Number(b.dataset.colorStart) - Number(a.dataset.colorStart))[0] || this.buttons[0];
      if (button) this.showColor(button.dataset.rs3Color);
    }

    showColor(id) {
      const panel = this.panels.find(item => item.dataset.rs3Panel === id);
      const purchasePanel = this.purchasePanels.find(item => item.dataset.rs3Purchase === id);
      const button = this.buttons.find(item => item.dataset.rs3Color === id);
      if (!panel || !purchasePanel || !button || id === this.currentId) return;
      this.currentId = id;
      this.panels.forEach(item => { item.hidden = item !== panel; });
      this.purchasePanels.forEach(item => { item.hidden = item !== purchasePanel; });
      this.buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      this.setText(this.label, button.dataset.colorLabel);
      if (this.status) {
        const price = purchasePanel.querySelector('[data-product-price]')?.textContent.trim().replace(/\s+/g, ' ');
        this.status.textContent = `${button.dataset.colorLabel}${price ? ` — ${price}` : ''}`;
      }
    }

    setText(element, text) {
      if (!element) return;
      const reveal = this.root?.clockReveal;
      if (reveal?.setText) reveal.setText(element, text);
      else if (element.textContent !== text) element.textContent = text;
    }

    select(id) {
      const button = this.buttons.find(item => item.dataset.rs3Color === id);
      if (!button) return;
      const stage = this.stages.findIndex(item => item.id === id);
      this.desiredStage = stage;
      if (this.enabled) {
        this.requestFrame();
      } else {
        this.settledStage = stage;
        this.playhead = this.stages[stage].frame;
        this.presentedFrame = this.playhead;
        this.showColor(id);
      }
    }

    onKeydown(event) {
      const button = event.target.closest('[data-rs3-color]');
      if (!button || !this.contains(button)) return;
      const index = this.buttons.indexOf(button);
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + this.buttons.length) % this.buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.buttons.length - 1;
      else return;
      event.preventDefault();
      event.stopPropagation();
      this.select(this.buttons[next].dataset.rs3Color);
      this.buttons[next].focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.transition = null;
      this.releaseMedia();
      this.removeAttribute('data-rs3-scroll-ready');
      this.buttons?.forEach(button => { button.disabled = true; });
    }
  });
}
